import {useTranslation} from 'react-i18next';
import React from 'react';
import {useQuery} from '@tanstack/react-query';
import {QUERY_KEYS} from '../../../../utils/queryKeys';
import ElementShadow from '../common/elementShadow';
import {TABS} from '../../form/part/tabs';
import {IElementCalculator} from '../../../../incase-domain/elements/calculator';
import DropdownMenuCompact from '../../../../incase-domain/components/dropdownMenuCompact/DropdownMenuCompact';
import Button from '../../../../incase-domain/components/button/Button';
import {fetchCalculators} from '../../../../service/calculator.service';
import {useHighlightedElement, useSetHighlightedElement} from '../../../../utils/hooks/slideUpdates/useSetSlideElement';
import AnimationForm from '../common/animationForm';

interface HighlightedCalculatorElementComponentProps {
    activeTab: string;
}

export default function HighlightedCalculatorElementComponent(props: HighlightedCalculatorElementComponentProps): JSX.Element {
    const {activeTab} = props;
    const {t} = useTranslation();
    const highlightedElement = useHighlightedElement() as IElementCalculator;
    const updateHighlightedElement = useSetHighlightedElement();

    const {data: calculators} = useQuery(QUERY_KEYS.calculator.lists(), () => fetchCalculators());

    const dropdownCalculatorItems = [
        calculators?.map((calc) => ({
            label: calc.name,
            onClick: () => updateHighlightedElement.single('calculatorId', calc._id),
        })) || [],
    ];

    const selectedCalculator = calculators?.find((calc) => calc._id === highlightedElement.calculatorId);

    if (activeTab === TABS.ADVANCED.id) {
        return (
            <>
                <ElementShadow highlightedElement={highlightedElement} highlightedElementChangeHandler={updateHighlightedElement} />
                <AnimationForm
                    appearAfter={highlightedElement.appearAfter || 0}
                    elementAnimations={highlightedElement.elementAnimations}
                    highlightedElementChangeHandler={updateHighlightedElement}
                />
            </>
        );
    }

    return (
        <div className="l-group--form u-mt-1 c-calculatorSidepanel">
            <div className="u-mb-2">
                <DropdownMenuCompact items={dropdownCalculatorItems} width="full">
                    <Button
                        className="c-sidepanel__dropdown_button"
                        trailing="solar:alt-arrow-down-linear"
                        size="xs"
                        variant="soft"
                        fullWidth
                    >
                        {calculators?.find((calc) => calc._id === highlightedElement.calculatorId)?.name ||
                            t('presEditPage.editSidebar.elements.calculator.select')}
                    </Button>
                </DropdownMenuCompact>
            </div>

            {selectedCalculator && (
                <>
                    <div className="l-group__item">
                        <label className="c-label">{t('presEditPage.editSidebar.elements.calculator.header')}</label>
                        <div className="c-sidepanel__input">
                            <input
                                type="text"
                                className="c-sidepanel__input_field"
                                value={selectedCalculator?.header}
                                // onChange={(e) => handleInputChange(e.target.value, field.variableName)}
                                readOnly
                            />
                        </div>
                    </div>

                    <div className="l-group__item">
                        <label className="c-label">{t('presEditPage.editSidebar.elements.calculator.variables')}</label>
                        {selectedCalculator?.fields?.map((field) => (
                            <div key={field._id} className="c-calculatorSidepanel__field">
                                <div className="c-sidepanel__input">
                                    <input
                                        type="text"
                                        className="c-sidepanel__input"
                                        value={field.name}
                                        // onChange={(e) => handleInputChange(e.target.value, field.variableName)}
                                        readOnly
                                    />
                                </div>
                                <div className="c-sidepanel__input">
                                    <input
                                        type="text"
                                        className="c-sidepanel__input"
                                        value={field.label}
                                        // onChange={(e) => handleInputChange(e.target.value, field.variableName)}
                                        readOnly
                                    />
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="l-group__item">
                        <label className="c-label">{t('presEditPage.editSidebar.elements.calculator.resultLabel')}</label>
                        <div className="c-sidepanel__input">
                            <input type="text" className="c-sidepanel__input_field" value={selectedCalculator?.resultLabel} readOnly />
                        </div>
                    </div>

                    <div className="l-group__item">
                        <label className="c-label">{t('presEditPage.editSidebar.elements.calculator.formula')}</label>
                        <div className="c-sidepanel__input">
                            <input type="text" className="c-sidepanel__input_field" value={selectedCalculator?.formula} readOnly />
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}
